import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, FileText, Package, Box } from "lucide-react";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import QuickCreateProductoDialog from "@/components/QuickCreateProductoDialog";

function emptyForm() {
  return {
    proveedor_id: "",
    numero_factura: "",
    autorizacion_sri: "",
    fecha_factura: new Date().toISOString().slice(0, 10),
    dias_credito: 60,
    recepcion_id: "",
    observaciones: "",
    lineas: [],
  };
}

function emptyLinea() {
  return {
    producto_comercial_id: "",
    producto_nombre: "",
    cantidad: 1,                  // valor que el usuario tipea (en la unidad elegida)
    costo_unitario: 0,            // costo por la unidad elegida (caja o unidad)
    iva_porc: 0,
    subtotal: 0,
    numero_lote: "",
    fecha_elaboracion: "",
    fecha_vencimiento_lote: "",
    registro_sanitario: "",
    _has_registro: false,
    _unidad_compra: "unidad",     // "unidad" | "caja"
    _unidades_por_caja: 1,        // unidades_por_presentacion del producto (≥1)
  };
}

export default function PurchaseInvoiceForm() {
  const navigate = useNavigate();
  const [provs, setProvs] = useState([]);
  const [prods, setProds] = useState([]);
  const [recepciones, setRecepciones] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null); // { lineaIndex } | null

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [p, pc, r] = await Promise.all([
          api.get("/masters/proveedores"),
          api.get("/masters/productos-comerciales"),
          api.get("/purchases/recepciones"),
        ]);
        if (!mounted) return;
        setProvs(p.data);
        setProds(pc.data);
        setRecepciones(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const fechaVencimientoCalc = useMemo(() => {
    if (!form.fecha_factura || form.dias_credito == null) return "";
    try {
      const d = new Date(form.fecha_factura + "T00:00:00");
      d.setDate(d.getDate() + (parseInt(form.dias_credito) || 0));
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  }, [form.fecha_factura, form.dias_credito]);

  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptyLinea()] }));

  const removeLinea = (i) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, x) => x !== i) }));

  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas];
    nl[i] = { ...nl[i], [field]: val };
    if (field === "producto_comercial_id") {
      const p = prods.find(x => x.id === val);
      if (p) {
        nl[i].producto_nombre = p.nombre_comercial;
        nl[i]._unidades_por_caja = parseInt(p.unidades_por_presentacion) || 1;
        // Costo inicial sugerido: en caja si el producto viene en caja, sino unidad
        const costoUnidad = parseFloat(p.precio_costo) || 0;
        const upc = nl[i]._unidades_por_caja;
        // Mantenemos el modo elegido (default "unidad"). Sugerimos costo coherente.
        if (!nl[i].costo_unitario) {
          nl[i].costo_unitario = nl[i]._unidad_compra === "caja"
            ? +(costoUnidad * upc).toFixed(4)
            : costoUnidad;
        }
        nl[i]._has_registro = !!(p.registro_sanitario && String(p.registro_sanitario).trim());
        if (nl[i]._has_registro) nl[i].registro_sanitario = "";
      }
    }
    nl[i].subtotal = +((parseFloat(nl[i].cantidad) || 0) * (parseFloat(nl[i].costo_unitario) || 0)).toFixed(4);
    return { ...f, lineas: nl };
  });

  // Cambiar de Unidad a Caja (o viceversa) recalculando cantidad y costo automáticamente
  const toggleUnidadCompra = (i, modo) => setForm(f => {
    const nl = [...f.lineas];
    const l = nl[i];
    if (!l || l._unidad_compra === modo) return f;
    const upc = parseInt(l._unidades_por_caja) || 1;
    const cantActual = parseFloat(l.cantidad) || 0;
    const costoActual = parseFloat(l.costo_unitario) || 0;
    if (modo === "caja") {
      // Estaba en unidad → pasar a caja
      nl[i] = {
        ...l,
        _unidad_compra: "caja",
        cantidad: upc > 0 ? +(cantActual / upc).toFixed(4) : cantActual,
        costo_unitario: +(costoActual * upc).toFixed(4),
      };
    } else {
      // Estaba en caja → pasar a unidad
      nl[i] = {
        ...l,
        _unidad_compra: "unidad",
        cantidad: +(cantActual * upc).toFixed(0),
        costo_unitario: upc > 0 ? +(costoActual / upc).toFixed(4) : costoActual,
      };
    }
    nl[i].subtotal = +((parseFloat(nl[i].cantidad) || 0) * (parseFloat(nl[i].costo_unitario) || 0)).toFixed(4);
    return { ...f, lineas: nl };
  });

  const onRecepcionChange = (recId) => {
    if (!recId || recId === "ninguna") {
      setForm(f => ({ ...f, recepcion_id: "" }));
      return;
    }
    const rec = recepciones.find(r => r.id === recId);
    if (!rec) return;
    const lineas = (rec.lineas || []).map(l => {
      const prod = prods.find(p => p.id === l.producto_comercial_id);
      return {
        producto_comercial_id: l.producto_comercial_id,
        producto_nombre: l.producto_nombre,
        cantidad: l.cantidad_recibida || l.cantidad || 1,
        costo_unitario: l.costo_unitario || 0,
        iva_porc: 0,
        subtotal: +((l.cantidad_recibida || l.cantidad || 0) * (l.costo_unitario || 0)).toFixed(4),
        numero_lote: l.numero_lote || "",
        fecha_elaboracion: l.fecha_elaboracion || "",
        fecha_vencimiento_lote: l.fecha_vencimiento || "",
        registro_sanitario: "",
        _has_registro: true,
        _unidad_compra: "unidad",
        _unidades_por_caja: parseInt(prod?.unidades_por_presentacion) || 1,
      };
    });
    setForm(f => ({ ...f, recepcion_id: recId, proveedor_id: rec.proveedor_id || f.proveedor_id, lineas }));
  };

  // Totales mostrados (en la unidad que el usuario tipeó — el subtotal es el mismo
  // independientemente del modo porque cant × costo es invariante a la conversión).
  const totales = useMemo(() => {
    const subtotal = form.lineas.reduce((s, l) => s + (+l.subtotal || 0), 0);
    const iva = form.lineas.reduce((s, l) => s + ((+l.subtotal || 0) * ((+l.iva_porc || 0) / 100)), 0);
    return { subtotal: +subtotal.toFixed(2), iva: +iva.toFixed(2), total: +(subtotal + iva).toFixed(2) };
  }, [form.lineas]);

  const cancelar = () => navigate("/compras");

  const guardarFactura = async () => {
    if (!form.proveedor_id) return toast.error("Seleccione proveedor");
    if (!form.numero_factura) return toast.error("Ingrese número de factura");
    if (form.lineas.length === 0) return toast.error("Agregue al menos una línea");
    if (!form.recepcion_id) {
      for (const l of form.lineas) {
        if (!l.numero_lote || !l.fecha_vencimiento_lote) {
          return toast.error("Cada línea requiere número de lote y fecha de vencimiento (caducidad).");
        }
      }
    }
    try {
      setSaving(true);
      const payload = {
        proveedor_id: form.proveedor_id,
        numero_factura: form.numero_factura,
        autorizacion_sri: form.autorizacion_sri || null,
        fecha_factura: form.fecha_factura,
        dias_credito: form.dias_credito != null ? parseInt(form.dias_credito) : null,
        fecha_vencimiento: fechaVencimientoCalc || null,
        recepcion_id: form.recepcion_id || null,
        observaciones: form.observaciones,
        lineas: form.lineas.map(l => {
          const upc = parseInt(l._unidades_por_caja) || 1;
          const esCaja = l._unidad_compra === "caja" && upc > 1;
          // Convertir SIEMPRE a unidades mínimas para que el stock FEFO calce
          const cantidadUnidades = esCaja
            ? Math.round((parseFloat(l.cantidad) || 0) * upc)
            : Math.round(parseFloat(l.cantidad) || 0);
          const costoUnidad = esCaja
            ? +((parseFloat(l.costo_unitario) || 0) / upc).toFixed(6)
            : +(parseFloat(l.costo_unitario) || 0).toFixed(6);
          return {
            producto_comercial_id: l.producto_comercial_id,
            producto_nombre: l.producto_nombre,
            cantidad: cantidadUnidades,
            costo_unitario: costoUnidad,
            iva_porc: parseFloat(l.iva_porc) || 0,
            subtotal: +(cantidadUnidades * costoUnidad).toFixed(4),
            numero_lote: l.numero_lote || null,
            fecha_elaboracion: l.fecha_elaboracion || null,
            fecha_vencimiento_lote: l.fecha_vencimiento_lote || null,
            registro_sanitario: (!l._has_registro && l.registro_sanitario) ? l.registro_sanitario : null,
          };
        }),
        ...totales,
      };
      const { data } = await api.post("/purchases/facturas", payload);
      toast.success(`Factura ${data.numero_factura} registrada · CxP creada`);
      navigate("/compras", { state: { tab: "facturas", newFactura: data } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-[#5c7368]">Cargando…</div>;
  }

  return (
    <div className="space-y-4 pb-40" data-testid="purchase-invoice-form-page">
      {/* Header sticky */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#f7f9f8]/95 backdrop-blur border-b border-[#d1dbd6]">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={cancelar} data-testid="purchase-form-back-btn" className="flex-shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c7368]">Compras / Facturas</div>
            <h1 className="text-lg md:text-2xl font-bold tracking-tight text-[#15241d] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <FileText className="inline mr-2 -mt-0.5" size={20} />
              Factura de compra
              {form.numero_factura ? (
                <span className="ml-2 text-[#205c45] font-mono" data-testid="purchase-form-numero">N° {form.numero_factura}</span>
              ) : (
                <span className="ml-2 text-[#5c7368]">nueva</span>
              )}
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" onClick={cancelar} disabled={saving} data-testid="purchase-form-cancel-btn-desktop">Cancelar</Button>
            <Button onClick={guardarFactura} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="purchase-form-save-btn-desktop">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : "Guardar factura"}
            </Button>
          </div>
        </div>
      </div>

      {/* Aviso */}
      <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-2 text-[11px] text-[#205c45]">
        Al guardar se crea automáticamente la <strong>CxP</strong> y se actualiza el stock con los lotes ingresados (FEFO).
        La retención SRI se sugiere a continuación.
      </div>

      {/* Cabecera */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label>Proveedor</Label>
          <ProveedorCombobox
            value={form.proveedor_id}
            onChange={(v) => setForm(f => ({ ...f, proveedor_id: v }))}
            proveedores={provs}
            placeholder="Buscar proveedor por razón social, RUC…"
            testId="fc-prov"
          />
        </div>
        <div>
          <Label>Recepción asociada (opcional)</Label>
          <Select value={form.recepcion_id || "ninguna"} onValueChange={onRecepcionChange}>
            <SelectTrigger data-testid="fc-recepcion-select"><SelectValue placeholder="Sin recepción" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ninguna">— Sin recepción —</SelectItem>
              {recepciones.map(r => <SelectItem key={r.id} value={r.id}>{r.numero} · {r.proveedor_nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nº Factura proveedor</Label>
          <Input value={form.numero_factura} onChange={(e) => setForm({ ...form, numero_factura: e.target.value })} placeholder="001-001-000123456" data-testid="fc-numero" />
        </div>
        <div>
          <Label>Autorización SRI (opcional)</Label>
          <Input
            value={form.autorizacion_sri}
            onChange={(e) => {
              const v = e.target.value.replace(/\s/g, "").slice(0, 49);
              setForm({ ...form, autorizacion_sri: v });
            }}
            maxLength={49}
            placeholder="49 dígitos clave acceso"
          />
          <div className="text-[10px] text-[#5c7368] mt-0.5 tabular-nums">
            {form.autorizacion_sri.length}/49
            {form.autorizacion_sri.length > 0 && form.autorizacion_sri.length < 49 && (
              <span className="text-[#b53a25] ml-1">· faltan {49 - form.autorizacion_sri.length}</span>
            )}
          </div>
        </div>
        <div>
          <Label>Fecha factura</Label>
          <Input type="date" value={form.fecha_factura} onChange={(e) => setForm({ ...form, fecha_factura: e.target.value })} data-testid="fc-fecha-factura" />
        </div>
        <div className="md:col-span-2">
          <Label>Días de crédito</Label>
          <div className="flex gap-2 items-center">
            <Input className="max-w-[140px]" type="number" min="0" max="365" value={form.dias_credito ?? ""} onChange={(e) => setForm({ ...form, dias_credito: e.target.value === "" ? null : parseInt(e.target.value) || 0 })} placeholder="60" data-testid="fc-dias-credito" />
            <span className="text-[11px] text-[#5c7368] whitespace-nowrap">→ vence {fechaVencimientoCalc || "—"}</span>
          </div>
        </div>
        <div className="md:col-span-3">
          <Label>Observaciones</Label>
          <Textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
        </div>
      </div>

      {/* LÍNEAS — Tarjetas responsive (multi-fila) */}
      <div className="border border-[#d1dbd6] rounded-md">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas</div>
          <Button size="sm" onClick={addLinea} className="bg-[#205c45] hover:bg-[#1a4a37] h-9" data-testid="fc-add-linea-btn">
            <Plus size={14} className="mr-1" />Agregar
          </Button>
        </div>

        <div className="p-3 space-y-3">
          {form.lineas.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-8 text-center text-sm text-[#8ba397]">
              Sin productos. Toque "Agregar" para empezar.
            </div>
          )}

          {form.lineas.map((l, i) => {
            const upc = parseInt(l._unidades_por_caja) || 1;
            const showCajaToggle = upc > 1;
            const esCaja = l._unidad_compra === "caja";
            const cantidad = parseFloat(l.cantidad) || 0;
            const costo = parseFloat(l.costo_unitario) || 0;
            // Pista de equivalencia
            const equivalencia = esCaja
              ? `≈ ${Math.round(cantidad * upc)} unidades · $${costo > 0 ? (costo / upc).toFixed(4) : "0.0000"}/u`
              : (showCajaToggle ? `≈ ${(cantidad / upc).toFixed(2)} cajas · $${(costo * upc).toFixed(4)}/caja` : "");

            return (
              <div key={i} className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-3 shadow-sm" data-testid={`fc-line-card-${i}`}>
                {/* Fila 1: Producto + delete */}
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Producto</Label>
                    <ProductoCombobox
                      value={l.producto_comercial_id}
                      onChange={(v) => updateLinea(i, "producto_comercial_id", v)}
                      productos={prods}
                      testId={`fc-prod-${i}`}
                      onCreateNew={() => setQuickCreate({ lineaIndex: i })}
                    />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeLinea(i)} className="text-[#b53a25] flex-shrink-0 mt-5" data-testid={`fc-delete-${i}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>

                {/* Fila 2: Toggle + Cantidad + Costo + IVA + Subtotal */}
                <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
                  {/* Toggle unidad/caja */}
                  {showCajaToggle ? (
                    <div className="col-span-2 md:col-span-3">
                      <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Modo</Label>
                      <div className="flex h-9 rounded-md border border-[#d1dbd6] overflow-hidden" data-testid={`fc-modo-${i}`}>
                        <button
                          type="button"
                          onClick={() => toggleUnidadCompra(i, "unidad")}
                          className={`flex-1 px-2 text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${!esCaja ? "bg-[#205c45] text-white" : "bg-white text-[#5c7368] hover:bg-[#f7f9f8]"}`}
                          data-testid={`fc-modo-unidad-${i}`}
                        >
                          <Package size={12} /> Unidad
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleUnidadCompra(i, "caja")}
                          className={`flex-1 px-2 text-xs font-semibold flex items-center justify-center gap-1 transition-colors border-l border-[#d1dbd6] ${esCaja ? "bg-[#205c45] text-white" : "bg-white text-[#5c7368] hover:bg-[#f7f9f8]"}`}
                          data-testid={`fc-modo-caja-${i}`}
                        >
                          <Box size={12} /> Caja ×{upc}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="hidden md:block md:col-span-3">
                      <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Modo</Label>
                      <div className="h-9 flex items-center px-2 text-xs text-[#5c7368] italic border border-[#d1dbd6] rounded-md bg-[#f7f9f8]">
                        <Package size={12} className="mr-1" /> Unidad
                      </div>
                    </div>
                  )}

                  <div className="col-span-1 md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">
                      Cant. {esCaja ? "(cajas)" : "(unid.)"}
                    </Label>
                    <Input
                      className="h-9 text-right tabular-nums"
                      type="number"
                      step={esCaja ? "0.01" : "1"}
                      value={l.cantidad}
                      onChange={(e) => updateLinea(i, "cantidad", esCaja ? (parseFloat(e.target.value) || 0) : (parseInt(e.target.value) || 0))}
                      data-testid={`fc-cant-${i}`}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-3">
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">
                      Costo {esCaja ? "/ caja" : "/ unidad"}
                    </Label>
                    <Input
                      className="h-9 text-right tabular-nums"
                      type="number"
                      step="0.0001"
                      value={l.costo_unitario}
                      onChange={(e) => updateLinea(i, "costo_unitario", parseFloat(e.target.value) || 0)}
                      data-testid={`fc-costo-${i}`}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">IVA %</Label>
                    <Select value={String(l.iva_porc)} onValueChange={(v) => updateLinea(i, "iva_porc", parseFloat(v))}>
                      <SelectTrigger className="h-9" data-testid={`fc-iva-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="15">15%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-1 md:col-span-2 flex flex-col">
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Subtotal</Label>
                    <div className="h-9 flex items-center justify-end text-base font-bold tabular-nums text-[#15241d]">
                      ${(+l.subtotal || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {equivalencia && (
                  <div className="text-[11px] text-[#5c7368] italic -mt-1" data-testid={`fc-equivalencia-${i}`}>{equivalencia}</div>
                )}

                {/* Fila 3: Lote + Vence + Elaboración + Reg. Sanitario */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-[#d1dbd6]">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">N° Lote</Label>
                    <Input
                      className="h-9"
                      value={l.numero_lote || ""}
                      onChange={(e) => updateLinea(i, "numero_lote", e.target.value)}
                      placeholder="A1234"
                      data-testid={`fc-lote-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Vence (caducidad)</Label>
                    <Input
                      className="h-9"
                      type="date"
                      value={l.fecha_vencimiento_lote || ""}
                      onChange={(e) => updateLinea(i, "fecha_vencimiento_lote", e.target.value)}
                      data-testid={`fc-vence-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">F. Elaboración</Label>
                    <Input
                      className="h-9"
                      type="date"
                      value={l.fecha_elaboracion || ""}
                      onChange={(e) => updateLinea(i, "fecha_elaboracion", e.target.value)}
                      data-testid={`fc-elab-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Reg. Sanitario</Label>
                    {l._has_registro ? (
                      <div className="h-9 flex items-center text-[11px] text-[#205c45] italic">✓ ya registrado</div>
                    ) : (
                      <Input
                        className="h-9"
                        value={l.registro_sanitario || ""}
                        onChange={(e) => updateLinea(i, "registro_sanitario", e.target.value)}
                        placeholder="GBE-1234"
                        data-testid={`fc-rs-${i}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Totales */}
          {form.lineas.length > 0 && (
            <div className="rounded-lg border border-[#d1dbd6] bg-[#f7f9f8] p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5c7368]">Subtotal</span><span className="tabular-nums">${totales.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#5c7368]">IVA</span><span className="tabular-nums">${totales.iva.toFixed(2)}</span></div>
              <div className="flex justify-between pt-1 border-t border-[#d1dbd6]"><span className="font-bold uppercase">Total</span><span className="text-xl font-bold tabular-nums">${totales.total.toFixed(2)}</span></div>
            </div>
          )}
        </div>

        <p className="text-[10px] text-[#5c7368] italic px-3 py-2 border-t border-[#d1dbd6]">
          El lote y la fecha de vencimiento son obligatorios para cada producto (excepto si la factura viene de una recepción previa con lotes ya creados). El registro sanitario sólo se solicita si el producto aún no lo tiene. Internamente el stock se convierte siempre a unidades mínimas (cajas × unidades por presentación).
        </p>
      </div>

      {/* Barra fija inferior (mobile) */}
      <div className="fixed bottom-[64px] left-0 right-0 z-[60] border-t border-[#d1dbd6] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-col text-right pr-1 leading-tight">
            <span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total</span>
            <span className="text-lg font-bold tabular-nums text-[#15241d]">${totales.total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2 flex-1 justify-end">
            <Button variant="outline" className="h-11 flex-1 max-w-[120px]" onClick={cancelar} disabled={saving} data-testid="purchase-form-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={guardarFactura} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37] h-11 flex-1" data-testid="purchase-form-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>
      {/* Quick create de producto comercial desde el combobox */}
      <QuickCreateProductoDialog
        open={!!quickCreate}
        onOpenChange={(o) => { if (!o) setQuickCreate(null); }}
        onCreated={(p) => {
          setProds(prev => [...prev, p].sort((a, b) => (a.nombre_comercial || "").localeCompare(b.nombre_comercial || "")));
          if (quickCreate?.lineaIndex != null) {
            updateLinea(quickCreate.lineaIndex, "producto_comercial_id", p.id);
          }
          setQuickCreate(null);
        }}
      />
    </div>
  );
}
